import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, Box, IconButton, TextField, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { addOption, removeOption, checkField, updateField } from "features/doctypesSlice";
import { useDialog } from "context/DialogContext";
import { useSnackbar } from "context/SnackbarContext";
import { useFocusContext } from "context/FocusContext";
import api from "services/api";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { RootState } from "services/store";
import DeleteIcon from '@mui/icons-material/Delete';
import { setOcrData } from "features/appStateSlice";

interface AutoCompleteFieldProps {
  field: any;
  doctypeKey: string;
  inputRef?: React.Ref<HTMLInputElement>;
  onBlurOcr: any;
  focusIndex: number;
}

const AutoCompleteField: React.FC<AutoCompleteFieldProps> = ({
  field,
  doctypeKey,
  inputRef,
  onBlurOcr,
  focusIndex
}) => {
  const { t } = useTranslation();
  const { showDialog } = useDialog();
  const { showSnackbar } = useSnackbar();
  const { focusField } = useFocusContext();
  const dispatch = useDispatch();

  const selectedDoctypeKey = useSelector((state: RootState) => state.doctypes.selectedDoctypeKey);
  const doctype = useSelector((state: RootState) => state.doctypes.doctypes.find((doc) => doc.Key === selectedDoctypeKey));
  const ocrData = useSelector((state: RootState) => state.appState.ocrData);

  const [lastInputValueAsked, setLastInputValueAsked] = useState<string | null>(null); // State to store the last input value asked

  const check = () => {
    dispatch(checkField({ doctypeKey, fieldKey: field.Key }));
  };

  const addNewOption = async (newValue: string) => {
    try {
      const res = await api.post(`/libtech/${field.Key}`, {
        value: newValue,
      });
      showSnackbar({
        message: res.data.Message,
        severity: "success",
      });
      dispatch(
        addOption({
          doctypeKey,
          fieldKey: field.Key,
          newOption: { Value: newValue, Label: newValue },
        })
      );
    } catch (error: any) {
      console.error(error);
      showSnackbar({
        message: error.data
          ? error.data.Message || t("internalError")
          : t("internalError"),
        severity: "error",
      });
    }
  };

  const onRemoveOption = async (value: string) => {
    try {
      const confirm = await showDialog({
        type: "confirmation",
        title: "Confirmation",
        message: t("dialog.wantToDeleteOption"),
        severity: "warning",
      });

      if (!confirm) return;

      const res = await api.delete(`/libtech/${field.Key}`, {
        data: {
          value: value
        }
      });
      showSnackbar({
        message: res.data.Message,
        severity: "success",
      });
      dispatch(
        removeOption({
          doctypeKey,
          fieldKey: field.Key,
          newOption: { Value: value, Label: value },
        })
      );
      dispatch(
        updateField({
          doctypeKey,
          fieldProperty: "Value",
          fieldKey: field.Key,
          value: "",
        })
      );
      dispatch(
        updateField({
          doctypeKey,
          fieldProperty: "InputValue",
          fieldKey: field.Key,
          value: "",
        })
      );
    } catch (error: any) {
      console.error(error);
      showSnackbar({
        message: error.data
          ? error.data.Message || t("internalError")
          : t("internalError"),
        severity: "error",
      });
    }
  };

  const onChange = (event: React.ChangeEvent<{}>, newValue: string | null) => {
    dispatch(
      updateField({
        doctypeKey,
        fieldProperty: "Value",
        fieldKey: field.Key,
        value: newValue || "",
      })
    );

    if(ocrData?.lastOcrTemplatePage) {
      let newOcrData = structuredClone(ocrData);
      if(newOcrData.lastOcrTemplatePage) {
        newOcrData.lastOcrTemplatePage = undefined;
        dispatch(setOcrData(newOcrData));
      }
    }
  };

  const onInputChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    if (field.Fixed) {
      dispatch(
        updateField({
          doctypeKey,
          fieldProperty: "InputValue",
          fieldKey: field.Key,
          value: newValue,
        })
      );
    } else {
      dispatch(
        updateField({
          doctypeKey,
          fieldProperty: "InputValue",
          fieldKey: field.Key,
          value: newValue,
        })
      );
      dispatch(
        updateField({
          doctypeKey,
          fieldProperty: "Value",
          fieldKey: field.Key,
          value: newValue,
        })
      );
    }

    if(ocrData?.lastOcrTemplatePage) {
      let newOcrData = structuredClone(ocrData);
      if(newOcrData.lastOcrTemplatePage) {
        newOcrData.lastOcrTemplatePage = undefined;
        dispatch(setOcrData(newOcrData));
      }
    }
  };

  const refocusInput = () => {
    //focusField(field.Pos - 1);
    focusField(focusIndex + 1);
  };

  const onBlur = async () => {
    if (field.Customizable) {
      let userInput = field.InputValue;
      if (userInput) {
        userInput = userInput.trim();
        
        dispatch(
          updateField({
            doctypeKey,
            fieldProperty: "InputValue",
            fieldKey: field.Key,
            value: userInput,
          })
        );

        const findOption = field.Options.find(
          (option: any) =>
            option.Value.toLowerCase() === userInput.toLowerCase() ||
            option.Label.toLowerCase() === userInput.toLowerCase()
        );
        if (!findOption && userInput.toLowerCase() !== lastInputValueAsked?.toLowerCase()) {
          await showDialog({
            type: "confirmation",
            title: t("confirmation.title"),
            message: `${t("autocomplete.confirmAddValue")}<br /><br />${userInput}`,
            severity: "info",
            onConfirm: () => {
              addNewOption(userInput);
            },
            onCancel: () => {},
          });
          refocusInput();
          setLastInputValueAsked(userInput);
        }
      }
    }

    check();

    if(onBlurOcr) {
      await onBlurOcr(field);
    }
  };

  return (
    <Autocomplete 
      componentsProps={{ popper: { style: { width: 'fit-component' }, placement: 'bottom-start' } }} 
      openOnFocus
      freeSolo={field.Customizable || !field.Fixed}
      options={field.Options || []}
      getOptionLabel={(option: any) =>
        typeof option === "string" ? option : option.Label
      }
      noOptionsText={t("fieldNoOptions")}
      value={field.Value || ""}
      inputValue={field.InputValue || ""}
      popupIcon={<ExpandMoreIcon />}
      forcePopupIcon={field && field.Options && field.Options.length > 0}
      renderInput={(params) => (
        <Box sx={{ display: "flex" }}>
          <Tooltip title={field.InputValue || ""} arrow placement="top">
            <TextField
              {...params}
              inputRef={inputRef}
              label={field.Label}
              variant="standard"
              required={field.Required}
              error={field.Error !== undefined && field.Error !== null}
              helperText={t(field.Error) || ""}
              onBlur={onBlur} 
            />
          </Tooltip>
          { doctype?.PosRefValues.find((value: string) => value == field.InputValue?.toLowerCase()) && (
            <Box
              sx={{
                width: 14,
                height: 14,
                flexShrink: 0,
                borderRadius: '3px',
                ml: 2,
                mt: '25px',
                alignSelf: "flex-start"
              }}
              style={{ backgroundColor: "red" }}
            />
          ) }
        </Box>
      )} 
      renderOption={(props: any, option: any, state: object, ownerState: object) => {
        return (
          <li {...props} key={props.key}>
            <Box
              sx={(t) => ({
                flexGrow: 1,
                '& span': {
                  color: '#8b949e',
                  ...t.applyStyles('light', {
                    color: '#586069',
                  }),
                },
              })}
            >
              {option.Label}
            </Box>
            <Box
              component="span"
              sx={{
                width: 14,
                height: 14,
                flexShrink: 0,
                borderRadius: '3px',
                ml: 2,
                mt: '2px',
              }}
              style={{ backgroundColor: doctype?.PosRefValues.find((value: string) => value == option.Label?.toLowerCase()) ? "red" : "transparent" }}
            />
            { (!field.Fixed && !field.OptionsFromDb) && (
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  ml: 2,
                  mt: '2px',
                }}>
                <Tooltip title={t("delete")} arrow placement="right">
                  <IconButton
                    aria-label="close"
                    onClick={() => onRemoveOption(option.Value)} 
                    sx={{ p: 0, m: 0 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            ) }
          </li>
        )
      }}
      onChange={onChange}
      onInputChange={onInputChange}
    />
  );
};

export default AutoCompleteField;